import React from "react"
import { CheckCircle, AlertCircle, XCircle, RefreshCw, ExternalLink, Clock } from "lucide-react"

import { trpc } from "../../lib/trpc"

// ============================================================================
// Types
// ============================================================================

interface MonitorStatus {
  id: string
  name: string
  url: string
  service: string
  status: string
  lastChecked: Date | string | null
  lastLatencyMs: number | null
  uptime24h: number
  hasOpenIncident: boolean
  incidentStart: Date | string | null
  recentChecks: { success: boolean; latencyMs: number | null }[]
}

// ============================================================================
// Sub-components
// ============================================================================

function OverallStatusBanner({ status }: { status: string }) {
  const configs: Record<string, { bg: string; icon: React.ReactNode; text: string; label: string }> = {
    operational: {
      bg: "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
      icon: <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />,
      text: "text-green-800 dark:text-green-300",
      label: "All Systems Operational",
    },
    degraded: {
      bg: "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800",
      icon: <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />,
      text: "text-yellow-800 dark:text-yellow-300",
      label: "Partial Outage",
    },
    down: {
      bg: "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800",
      icon: <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />,
      text: "text-red-800 dark:text-red-300",
      label: "Major Outage",
    },
  }

  const config = configs[status] || configs.operational

  return (
    <div className={`flex items-center gap-3 rounded-xl border p-5 ${config.bg}`}>
      {config.icon}
      <span className={`text-lg font-semibold ${config.text}`}>{config.label}</span>
    </div>
  )
}

function UptimeBar({ checks }: { checks: { success: boolean; latencyMs: number | null }[] }) {
  if (checks.length === 0) {
    return (
      <div className="flex gap-0.5">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="h-5 w-2 rounded-sm bg-neutral-200 dark:bg-neutral-700" />
        ))}
      </div>
    )
  }

  return (
    <div className="flex gap-0.5">
      {checks.map((check, i) => (
        <div
          key={i}
          title={check.latencyMs ? `${check.latencyMs}ms` : undefined}
          className={`h-5 w-2 rounded-sm ${
            check.success ? "bg-green-400 dark:bg-green-500" : "bg-red-400 dark:bg-red-500"
          }`}
        />
      ))}
    </div>
  )
}

function MonitorRow({ monitor }: { monitor: MonitorStatus }) {
  const uptimeColor =
    monitor.uptime24h >= 99
      ? "text-green-600 dark:text-green-400"
      : monitor.uptime24h >= 95
        ? "text-yellow-600 dark:text-yellow-400"
        : "text-red-600 dark:text-red-400"

  const statusConfig: Record<string, { icon: React.ReactNode; label: string; labelColor: string }> = {
    up: {
      icon: <span className="inline-block h-2 w-2 rounded-full bg-green-400 dark:bg-green-500" />,
      label: "Operational",
      labelColor: "text-green-700 dark:text-green-400",
    },
    down: {
      icon: <span className="inline-block h-2 w-2 rounded-full bg-red-400 dark:bg-red-500" />,
      label: "Outage",
      labelColor: "text-red-700 dark:text-red-400",
    },
    unknown: {
      icon: <span className="inline-block h-2 w-2 rounded-full bg-neutral-400" />,
      label: "Unknown",
      labelColor: "text-neutral-500",
    },
  }

  const statusConf = statusConfig[monitor.status] || statusConfig.unknown

  const incidentDuration = monitor.incidentStart
    ? formatDuration(Math.floor((Date.now() - new Date(monitor.incidentStart).getTime()) / 1000))
    : null

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-neutral-900 dark:text-neutral-100">{monitor.name}</span>
          {monitor.hasOpenIncident && (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/50 dark:text-red-400">
              INCIDENT {incidentDuration ? `(${incidentDuration})` : ""}
            </span>
          )}
        </div>
        {monitor.lastLatencyMs && (
          <span className="text-xs text-neutral-500 dark:text-neutral-400">{monitor.lastLatencyMs}ms</span>
        )}
      </div>

      <div className="hidden items-center gap-2 md:flex">
        <UptimeBar checks={monitor.recentChecks} />
      </div>

      <div className="flex w-28 items-center justify-end gap-1.5">
        {statusConf.icon}
        <span className={`text-sm font-medium ${statusConf.labelColor}`}>{statusConf.label}</span>
      </div>

      <div className="w-16 text-right">
        <span className={`text-sm font-semibold tabular-nums ${uptimeColor}`}>{monitor.uptime24h}%</span>
      </div>
    </div>
  )
}

function ServiceGroup({ serviceName, monitors }: { serviceName: string; monitors: MonitorStatus[] }) {
  const allUp = monitors.every((m) => m.status === "up")
  const anyDown = monitors.some((m) => m.status === "down")

  const groupStatus = anyDown ? "down" : allUp ? "up" : "unknown"
  const groupIcon =
    groupStatus === "up" ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : groupStatus === "down" ? (
      <XCircle className="h-4 w-4 text-red-500" />
    ) : (
      <AlertCircle className="h-4 w-4 text-neutral-400" />
    )

  return (
    <div className="rounded-xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
      {/* Group header */}
      <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3 dark:border-neutral-800">
        <div className="flex items-center gap-2">
          {groupIcon}
          <span className="text-sm font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400">
            {serviceName}
          </span>
        </div>
        <span className="text-xs text-neutral-400">{monitors.length} services</span>
      </div>

      {/* Monitor rows */}
      <div className="divide-y divide-neutral-50 px-5 dark:divide-neutral-800">
        {monitors.map((monitor) => (
          <MonitorRow key={monitor.id} monitor={monitor} />
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Helpers
// ============================================================================

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
}

function formatRelativeTime(dateStr: Date | string | null): string {
  if (!dateStr) return "Never"
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

// ============================================================================
// Main Component
// ============================================================================

export default function StatusPage() {
  const { data, isLoading, error, refetch, dataUpdatedAt } = trpc.uptimeMonitors.getPublicStatus.useQuery(undefined, {
    refetchInterval: 60_000, // Auto-refresh every minute
    retry: 2,
  })

  const lastRefreshed = dataUpdatedAt ? new Date(dataUpdatedAt) : null

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
        <div className="mx-auto max-w-4xl px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">AIX System Status</h1>
              <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
                Real-time uptime monitoring for all AIX services
              </p>
            </div>

            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          {lastRefreshed && (
            <div className="mt-2 flex items-center gap-1 text-xs text-neutral-400">
              <Clock className="h-3 w-3" />
              Last updated {formatRelativeTime(lastRefreshed.toISOString())} · Auto-refreshes every minute
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-8">
        {/* Loading state */}
        {isLoading && (
          <div className="space-y-4">
            <div className="h-16 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-800" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-800" />
            ))}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-800 dark:bg-red-900/20">
            <XCircle className="mx-auto mb-2 h-8 w-8 text-red-400" />
            <p className="text-red-700 dark:text-red-300">Failed to load status data.</p>
            <button onClick={() => refetch()} className="mt-2 text-sm text-red-600 underline dark:text-red-400">
              Try again
            </button>
          </div>
        )}

        {/* Status data */}
        {data && (
          <div className="space-y-6">
            {/* Overall status */}
            <OverallStatusBanner status={data.overallStatus} />

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "Total Services", value: data.summary.total, color: "text-neutral-700 dark:text-neutral-300" },
                { label: "Operational", value: data.summary.up, color: "text-green-700 dark:text-green-400" },
                {
                  label: "Down",
                  value: data.summary.down,
                  color: data.summary.down > 0 ? "text-red-700 dark:text-red-400" : "text-neutral-400",
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-neutral-200 bg-white p-4 text-center dark:border-neutral-800 dark:bg-neutral-900"
                >
                  <div className={`text-2xl font-bold tabular-nums ${stat.color}`}>{stat.value}</div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-6 text-xs text-neutral-500 dark:text-neutral-400">
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 rounded-sm bg-green-400" />
                Operational
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 rounded-sm bg-red-400" />
                Outage
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 rounded-sm bg-neutral-300" />
                No data
              </div>
              <span className="ml-auto">← Last 10 checks</span>
            </div>

            {/* Services grouped */}
            {Object.entries(data.services).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(data.services).map(([service, monitors]) => (
                  <ServiceGroup key={service} serviceName={service} monitors={monitors as MonitorStatus[]} />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-neutral-200 bg-white p-8 text-center dark:border-neutral-800 dark:bg-neutral-900">
                <p className="text-neutral-500 dark:text-neutral-400">No monitors configured yet.</p>
                <p className="mt-1 text-sm text-neutral-400 dark:text-neutral-500">
                  Run{" "}
                  <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs dark:bg-neutral-800">
                    bun run .claude/skills/api-command-center/command-center.ts seed
                  </code>{" "}
                  to add default monitors.
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="pt-4 text-center text-xs text-neutral-400 dark:text-neutral-500">
              <p>
                Powered by{" "}
                <a
                  href="https://aixlabs.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 underline"
                >
                  AIX Labs
                  <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
